// @givmo/auth — which sign-in methods leave an email the backend OTP must repair.
//
// GCIP does not treat every provider's email as verified: Google (and Apple) attest
// `email_verified`, so those users arrive with `emailVerified: true`; **microsoft.com users are
// minted with `emailVerified: false`** — Microsoft does not attest verification, and its raw email
// claim is admin-mutable (the nOAuth issue), so trusting the claim instead of repairing the flag
// would be an account-takeover vector. Password accounts assert nothing about their address either.
//
// The repair is Givmo's own backend OTP (`POST /users/me/confirm_email` sets the Firebase flag for
// ANY user — inbox proof), so the question every gate actually asks is: "are ALL of this user's
// sign-in methods ones whose unverified email that OTP should repair?" A user also linked to an
// attesting provider is left alone — their address was verified at that provider.
//
// NF-FIX24: and every OTHER unverified account with an email that no attesting provider linked to it
// vouches for. Both apps sign people up by phone and never verify the email they add, and since
// NF-FIX19 an email changed in an app is unverified until proven; such an account (providers
// `phone`, sometimes beside an attesting one whose address is no longer the account's) had no way to
// prove its address on the web, so every verified-email feature refused it for good. The OTP proves
// the account's own address, so it repairs them too. An attesting provider vouches only for its own
// address: Google's word on `old@` is no proof of `new@`.
//
// Consumers: the charity portal's route guard (`requiresEmailVerification`) and the admin portal's
// provider sign-in/up flows, which route an unverified Microsoft admin through the OTP screen
// BEFORE company onboarding (the EP-SUITE2 join-or-create endpoints refuse unverified callers, and
// the funnel's join detection needs a trustworthy address).

import type { User } from 'firebase/auth';

/** The sign-in methods whose unverified email the backend OTP repairs. */
const OTP_REPAIRABLE_PROVIDERS: ReadonlySet<string> = new Set(['password', 'microsoft.com']);

/** The sign-in methods that attest the email they carry (GCIP mints them verified). */
const ATTESTING_PROVIDERS: ReadonlySet<string> = new Set(['google.com', 'apple.com']);

/** An address as the backend compares one: trimmed, lowercased (NF-FIX19). */
function comparableEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

/**
 * Whether this Firebase user's email is unverified and the backend OTP should repair it: every
 * linked sign-in method is one whose email the OTP repairs (password, microsoft.com), or — NF-FIX24 —
 * the account has an email that no linked attesting provider (google.com / apple.com) vouches for
 * (a phone-linked app account, an address moved since). `false` for a verified user, a
 * provider-less record, an account with no email, or one whose own address an attesting provider
 * carries.
 */
export function needsBackendOtpVerification(user: User): boolean {
  if (user.emailVerified !== false) return false;
  const providers = user.providerData;
  if (providers.length === 0) return false;
  if (providers.every((provider) => OTP_REPAIRABLE_PROVIDERS.has(provider.providerId))) return true;
  const email = comparableEmail(user.email);
  if (email === '') return false;
  return !providers.some(
    (provider) =>
      ATTESTING_PROVIDERS.has(provider.providerId) && comparableEmail(provider.email) === email,
  );
}
